import CryptoJS from 'crypto-js'

// Get encryption key from environment variable
const ENCRYPTION_KEY = process.env.VUE_APP_ENCRYPTION_KEY || 'your-default-32-byte-secret-key-here'

// Convert hex key to WordArray, or use utf8 if not hex
let parsedKey
try {
  // Try to parse as hex first (if it's a 64-character hex string)
  if (ENCRYPTION_KEY.length === 64 && /^[0-9a-fA-F]{64}$/.test(ENCRYPTION_KEY)) {
    parsedKey = CryptoJS.enc.Hex.parse(ENCRYPTION_KEY)
  } else {
    // Fallback to UTF-8 parsing and pad/truncate to 32 bytes
    const keyStr = ENCRYPTION_KEY.padEnd(32, '0').slice(0, 32)
    parsedKey = CryptoJS.enc.Utf8.parse(keyStr)
  }
} catch (error) {
  // Fallback to UTF-8 if hex parsing fails
  const keyStr = ENCRYPTION_KEY.padEnd(32, '0').slice(0, 32)
  parsedKey = CryptoJS.enc.Utf8.parse(keyStr)
}

/**
 * Encrypt a password using AES encryption
 * @param {string} password - The password to encrypt
 * @returns {string} - The encrypted password (IV + encrypted data)
 */
export function encryptPassword(password) {
  try {
    if (!password) {
      return ''
    }
    
    // Generate a random IV for each encryption
    const iv = CryptoJS.lib.WordArray.random(16)
    
    // Encrypt the password
    const encrypted = CryptoJS.AES.encrypt(password, parsedKey, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    })
    
    // Combine IV and encrypted data
    const combined = iv.concat(encrypted.ciphertext)
    
    // Return as base64 string
    return CryptoJS.enc.Base64.stringify(combined)
  } catch (error) {
    console.error('Encryption error:', error)
    throw new Error('Failed to encrypt password')
  }
}

/**
 * Check if a string appears to be encrypted
 * @param {string} str - The string to check
 * @returns {boolean} - True if the string appears to be encrypted
 */
export function isEncrypted(str) {
  if (!str || typeof str !== 'string') {
    return false
  }
  
  try {
    // Check if it's a valid base64 string of appropriate length
    const decoded = CryptoJS.enc.Base64.parse(str)
    // Encrypted password should be at least 32 bytes (16-byte IV + 16+ bytes encrypted data)
    return decoded.sigBytes >= 32
  } catch (error) {
    return false
  }
}

/**
 * Validate that encryption is working properly
 * @returns {boolean} - True if encryption/decryption test passes
 */
export function validateEncryption() {
  try {
    const testPassword = 'test123'
    const encrypted = encryptPassword(testPassword)
    return encrypted && encrypted.length > 0 && isEncrypted(encrypted)
  } catch (error) {
    console.error('Encryption validation failed:', error)
    return false
  }
}